import { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AuthContext from "@/contexts/AuthContext";
import { ClubsService } from "@/core/http/services/ClubsService";

const SuperAdminClubCreate = () => {
  const navigate = useNavigate();
  const { userLoggedData } = useContext(AuthContext);

  const role = userLoggedData?.role?.toUpperCase?.() || "";
  const isAllowed = role === "SUPER_ADMIN";

  const [form, setForm] = useState({
    name: "",
    logoLightUrl: "",
    logoDarkUrl: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit = useMemo(() => form.name.trim().length > 0, [form.name]);

  const handleChange = useCallback(
    (field) => (event) => {
      const value = event.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canSubmit) return;

      setLoading(true);
      setError(null);

      const payload = {
        name: form.name.trim(),
        logoLightUrl: form.logoLightUrl.trim() || null,
        logoDarkUrl: form.logoDarkUrl.trim() || null,
      };

      const { status, message } = await ClubsService.create(payload);

      if (status !== 200 && status !== 201) {
        setError(message || "Erro ao criar clube.");
        setLoading(false);
        return;
      }

      setLoading(false);
      navigate("/super-admin/clubs");
    },
    [canSubmit, form.logoDarkUrl, form.logoLightUrl, form.name, navigate],
  );

  if (!userLoggedData) return null;
  if (!isAllowed) {
    return (
      <Box sx={{ px: 2, py: 4 }}>
        <Alert severity="error">Acesso negado.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
        bgcolor: "background.default",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 720,
          p: 3,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>
              Criar novo clube
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Preencha os dados para criar um clube.
            </Typography>
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={1.5}>
              <TextField
                label="Nome"
                value={form.name}
                onChange={handleChange("name")}
                required
                fullWidth
              />
              <TextField
                label="Logo Light URL"
                value={form.logoLightUrl}
                onChange={handleChange("logoLightUrl")}
                fullWidth
                placeholder="https://..."
              />
              <TextField
                label="Logo Dark URL"
                value={form.logoDarkUrl}
                onChange={handleChange("logoDarkUrl")}
                fullWidth
                placeholder="https://..."
              />

              <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate("/super-admin/clubs")}
                  sx={{ textTransform: "none", fontWeight: 800 }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!canSubmit || loading}
                  sx={{ textTransform: "none", fontWeight: 800, ml: "auto" }}
                >
                  {loading ? "Criando..." : "Criar clube"}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default SuperAdminClubCreate;

